import { Icon } from "@iconify/react/dist/iconify.js";
import { Close } from "@mui/icons-material";
import { Alert, Box, Button, Collapse, Grow, IconButton, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from '../../assets/logos/icare_fulllogo.png';
import { ButtonContentWithLoader } from "../../components/inputs/ButtonContentWithLoader";
import PasswordInput from "../../components/inputs/PasswordInput";
import { isOTPValid, isValidEmail, sendReactGAPageView } from "../../consts/helpers";

export default function ForgetPassword() {
	const { pathname } = useLocation();
	useEffect(() => {
        sendReactGAPageView(pathname, 'Reset Password');
    }, [pathname])

    const navigate = useNavigate();

    // Inputs
    const [email, setEmail] = useState('');
    const [otp, setOTP] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Stage flags
    const [isLoading, setLoading] = useState(false);
    const [passwordChangeStage, setStage] = useState(1); // 1: email, 2: verification code, 3: new password
    const [isPasswordMatchValid, setIsPasswordMatchValid] = useState(false);
    const [isPasswordRegexValid, setIsPasswordRegexValid] = useState(false);
    const [isEmailValid, setIsEmailValid] = useState(false);
    const [isOtpCorrect, setIsOtpCorrect] = useState(false);

    // Response flags
    const [isAlertOpen, setOpen] = useState(false);
    const [apiResponse, setResponse] = useState({});
    const setAlertResponse = (severity, message) => {
        setResponse({ severity: severity, message: message });
        setOpen(Boolean(message));
        setTimeout(() => {
            setOpen(false);
        }, 20000);
    }
    const setErrorMsg = (msg) => {
        if (msg) setAlertResponse('warning', msg);
    }

    // Handle input checks
    const handleEmailChange = (e) => {
        const emailInput = e.target.value.toLowerCase();
        setEmail(emailInput);
        setIsEmailValid(isValidEmail(emailInput));  
    };
    const handleConfirmPassword = (e, isConfirmer=false) => {
        const confirmPasswordInput = e.target.value;
        if (isConfirmer) {
            setConfirmPassword(confirmPasswordInput);
            setIsPasswordMatchValid(confirmPasswordInput === password);  
        } else {
            setPassword(confirmPasswordInput);
            setIsPasswordMatchValid(confirmPasswordInput === confirmPassword);  
        }
    };

    const callSignUpAPI = async (stage) => {
        const url = process.env.REACT_APP_USERACCESS_LAMBDA;
        const response = await fetch(url, {
            method: 'PUT',
            body: JSON.stringify({
                'stage': stage,
                'email': email,
                'code': otp,
                'password': password,
            }),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        })
        return response.json();
    }

    const handlePasswordChange = (e, overrideStage1 = false) => {
        e.preventDefault();
        var stage = overrideStage1 ? 1 : passwordChangeStage;

        // Catch potential errors from incomplete input fields
        if (stage === 1 && (email === "" || !isValidEmail(email))) {
            setErrorMsg("Please enter a valid email.");
            return;
        } else if (stage === 2 && otp === "") {
            setErrorMsg("Please enter the verification code sent to your email.");
            return;
        } else if (stage === 3) {
            if (password === "") {
                setErrorMsg("Please enter a password.");
                return;
            }
            else if (password === "") {
                setErrorMsg("Please enter a password.");
                return;
            }
        } 
        setAlertResponse('success','')
        setLoading(true);
        callSignUpAPI(stage).then(
            function(data) {
                setLoading(false);
                if (data?.passStage) {
                    if (stage === 1) {
                        setAlertResponse('success','An OTP has been sent to your email. Enter it above to verify your access and reset your password.')
                        setStage(2);
                    } else if (stage === 2) {
                        setIsOtpCorrect(true); 
                        setStage(3);
                    } else if (stage === 3) {
                        navigate("/login",
                            {
                                state : {
                                    severity: 'success',
                                    message: "Password was successfully changed. You may now log in with your new credentials."
                                }
                            });
                        setStage(4);
                    } 
                } else {
                    setErrorMsg(data?.message);
                }
            }
        ).catch(
            function(error) {
                setErrorMsg(error);
            }
        );
    }

    return (
        <Stack spacing={3} sx={{ padding: 2 }}>
            <Box className="centered-container">
                <img src={logo} alt='icare app logo' style={{ maxWidth: 300 }}/>
            </Box>
            <Typography variant="h4" align="center" className='green-text-color'>Reset Password</Typography>
            { passwordChangeStage===4 ?
            <>
            <Box sx={{ textAlign: 'center'  }}>
                <Typography variant="h1"><Icon icon='gg:check-o' color='var(--pri-green)'/></Typography>
                <Typography variant="body1">Your password has been<br/> changed successfully!</Typography>
            </Box>
            <Button 
                className={'pill_btn pri_btn'}
                fullWidth 
                onClick={() => navigate('/login')}
            >
                Proceed to log in
            </Button>
            </>
            :
            <>
                <TextField
                    className='rounded-input' 
                    fullWidth
                    label="Email"
                    variant="outlined"
                    value={email}
                    onChange={handleEmailChange}
                    margin="normal"
                    required
                    disabled={passwordChangeStage>1}
                    slotProps={{
                        input: {
                            endAdornment:(
                                <Grow in={isOtpCorrect}><Icon icon='gg:check-o' color='var(--pri-green)' fontSize={'32px'} /></Grow>
                            )
                        }
                    }}
                    error={!isEmailValid && email.length > 0}  
                    helperText={!isEmailValid && email.length > 0 ? "Please enter a valid email address" : ""} 
                />
                {
                    passwordChangeStage===1 ?
                        <Button 
                            className={`pill_btn ${isEmailValid ? 'pri_btn' : ''}`}
                            fullWidth 
                            onClick={handlePasswordChange}
                            disabled={!isEmailValid}
                        >
                            { ButtonContentWithLoader(isLoading, 'Get Verification Code') }
                        </Button>
                    : passwordChangeStage===2 ?
                        <>
                            <TextField
                                className='rounded-input' 
                                fullWidth
                                label="OTP"
                                variant="outlined"
                                value={otp}
                                onChange={(e) => setOTP(e.target.value)}
                                margin="normal"
                                required
                                error={!isOTPValid(otp)}  
                                helperText={!isOTPValid(otp) ? "Invalid code. Please enter the 4-digit OTP sent to your email." : ""} 
                            />
                            <Button 
                                className="pill_btn pri_btn"
                                onClick={handlePasswordChange}
                                fullWidth
                            >
                                { ButtonContentWithLoader(isLoading, 'Verify') }
                            </Button>
                        </>
                    : passwordChangeStage===3 ?
                    <>
                    <PasswordInput 
                        password={password}
                        withConfirmation={true}
                        confirmPassword={confirmPassword}
                        changeHandler={handleConfirmPassword}
                        isPasswordMatchValid={isPasswordMatchValid}
                        setIsPasswordRegexValid={setIsPasswordRegexValid}
                    />
                    <Button 
                        className={`pill_btn ${email && isPasswordMatchValid && isPasswordRegexValid ? 'pri_btn' : ''}`} 
                        fullWidth 
                        onClick={handlePasswordChange}
                        disabled={!email || !isPasswordMatchValid || !isPasswordRegexValid }
                    >
                        { ButtonContentWithLoader(isLoading, 'Reset Password') }
                    </Button>
                    </>
                    :
                    <></>
                }
                <Collapse in={isAlertOpen}>
                    <Alert
                    action={
                        <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={() => {
                            setOpen(false);
                        }}
                        >
                        <Close fontSize="inherit" />
                        </IconButton>
                    }
                    sx={{ mt: 2, borderRadius: '24px' }}
                    severity={apiResponse.severity}
                    >
                    {apiResponse.message}
                    </Alert>
                </Collapse>
                {
                    passwordChangeStage!==2 &&
                    <Typography variant="body2" align="center" sx={{ mt: 2 }}>
                        <Link to="/login"> Back to Login </Link>
                    </Typography>
                }
            </>    
            }
        </Stack>
    );
}
